/**
 * ChaptersList Component
 *
 * Dropdown menu displaying all chapters with:
 * - Chapter number and title
 * - Start time and duration
 * - Active chapter highlighting
 * - Click to jump to chapter
 */

import { List } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getPlayerActions } from "@/lib/player/player.store";
import { useTranslation } from "react-i18next";

interface ChaptersListProps {
	chapters: Array<{ text: string; startTime: number; endTime: number }>;
	activeChapterIndex: number;
	showChapters: boolean;
	totalDuration: number;
	formatTime: (seconds: number) => string;
}

export function ChaptersList({
	chapters,
	activeChapterIndex,
	showChapters,
	totalDuration,
	formatTime,
}: ChaptersListProps) {
	const { t } = useTranslation();

	return (
		<DropdownMenu
			open={showChapters}
			onOpenChange={getPlayerActions().setShowChapters}
		>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="h-9 w-9 text-white hover:text-primary hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-150 rounded-full"
					aria-label={t("aria.chapters")}
				>
					<List className="h-7 w-7" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-80 p-0 max-w-[320px]">
				{/* Header */}
				<div className="p-3 border-b border-border">
					<p className="text-sm font-semibold">
						{t("player.chaptersCount", { count: chapters.length })}
					</p>
					<p className="text-xs text-muted-foreground mt-0.5">
						{t("player.totalDuration", { duration: formatTime(totalDuration) })}
					</p>
				</div>

				{/* Scrollable chapters list */}
				<ScrollArea className="h-80 w-80 max-w-full">
					<div className="p-2 w-full box-border">
						{chapters.map((chapter, index) => (
							<button
								key={chapter.text}
								type="button"
								className={[
									"block w-75 text-left p-2.5 rounded-lg transition-colors mb-1 hover:bg-accent",
									index === activeChapterIndex
										? "bg-primary/20 border border-primary"
										: "hover:bg-accent",
								].join(" ")}
								onClick={() => {
									const actions = getPlayerActions();
									actions.goToChapter(index);
									actions.setShowChapters(false);
								}}
							>
								<div className="flex items-start gap-2">
									{/* Chapter number badge */}
									<div
										className={[
											"flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-semibold",
											index === activeChapterIndex
												? "bg-primary text-primary-foreground"
												: "bg-muted text-muted-foreground",
										].join(" ")}
									>
										{index + 1}
									</div>

									{/* Chapter details */}
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium truncate overflow-hidden">
											{chapter.text}
										</p>
										<div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground overflow-hidden">
											<span className="shrink-0">
												{formatTime(chapter.startTime)}
											</span>
											<span className="shrink-0">•</span>
											<span className="truncate">
												{formatTime(chapter.endTime - chapter.startTime)}{" "}
												{t("common.duration")}
											</span>
										</div>
									</div>
								</div>
							</button>
						))}
					</div>
				</ScrollArea>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
